import { NavigateFunction } from 'react-router-dom';

/**
 * Navigation utility to ensure consistent client-side routing
 * Use this instead of window.location.href for internal navigation
 */
export class NavigationHelper {
  private static navigate: NavigateFunction;

  static setNavigate(navigate: NavigateFunction) {
    this.navigate = navigate;
  }

  /**
   * Navigate to internal routes using React Router
   * @param path - Internal path like '/calculator' or '/?tab=blog'
   */
  static navigateTo(path: string) {
    if (this.navigate) {
      this.navigate(path);
      // Scroll to top after navigation
      setTimeout(() => window.scrollTo(0, 0), 0);
    } else {
      console.warn('NavigationHelper: navigate function not set, falling back to location.href');
      window.location.href = path;
    }
  }

  /**
   * Navigate to external URLs (use window.location.href)
   * @param url - Full external URL like 'https://external-site.com'
   */
  static navigateToExternal(url: string) {
    window.location.href = url;
  }

  /**
   * Get current URL for social sharing, SEO, etc.
   * This is the ONLY legitimate use of window.location.href for reading
   */
  static getCurrentUrl(): string {
    return window.location.href;
  }

  /**
   * Check if a URL is internal to our app
   * @param url - URL to check
   */
  static isInternalUrl(url: string): boolean {
    return url.startsWith('/') || url.startsWith('?') || url.startsWith('#');
  }

  /**
   * Smart navigation that chooses the right method
   * @param url - URL to navigate to
   */
  static smartNavigate(url: string) {
    if (this.isInternalUrl(url)) {
      this.navigateTo(url);
    } else {
      this.navigateToExternal(url);
    }
  }

  /**
   * Navigate with loading state management
   * @param path - Path to navigate to
   * @param setLoading - Loading state setter function
   * @param delay - Optional delay in ms (default: 100)
   */
  static navigateWithLoading(
    path: string, 
    setLoading?: (loading: boolean) => void,
    delay: number = 100
  ) {
    if (setLoading) setLoading(true);
    
    setTimeout(() => {
      this.navigateTo(path);
      if (setLoading) setLoading(false);
    }, delay);
  }
}

/**
 * Hook for easier navigation in components
 * Usage: const nav = useAppNavigation()
 */
export const useAppNavigation = (navigate: NavigateFunction) => {
  NavigationHelper.setNavigate(navigate);
  
  return {
    navigateTo: NavigationHelper.navigateTo,
    navigateToExternal: NavigationHelper.navigateToExternal,
    getCurrentUrl: NavigationHelper.getCurrentUrl,
    smartNavigate: NavigationHelper.smartNavigate,
    navigateWithLoading: NavigationHelper.navigateWithLoading,
  };
};

/**
 * Common navigation patterns for the trading website
 */
export const TradingRoutes = {
  // Main pages
  HOME: '/',
  BLOG: '/?tab=blog',
  CHARTS: '/?tab=charts',
  CALENDAR: '/?tab=calendar',
  MENTORING: '/?tab=mentoring',
  ADMIN: '/?tab=admin',
  
  // Educational resources
  EDUCATION: '/educational-resources',
  FUTURES_GUIDE: '/futures-trading-guide',
  DAY_VS_SWING: '/day-trading-vs-swing-trading',
  MARGIN_REQUIREMENTS: '/margin-requirements',
  COMMODITY_BASICS: '/commodity-trading-basics',
  OPTIONS_VS_FUTURES: '/options-vs-futures',
  DAY_TRADING_STRATEGIES: '/day-trading-strategies',
  SWING_TRADING_STRATEGIES: '/swing-trading-strategies',
  
  // Tools
  CALCULATORS: '/calculators',
  POSITION_SIZE_CALC: '/calculators/position-size',
  RISK_REWARD_CALC: '/calculators/risk-reward',
  PROFIT_LOSS_CALC: '/calculators/profit-loss',
  COMPOUND_CALC: '/calculators/compound-interest',
  FIBONACCI_CALC: '/calculators/fibonacci',
  TRADING_JOURNAL: '/trading-journal',
  PSYCHOLOGY: '/psychology',
  SCREENER: '/screener',
  
  // Legal/Info pages
  CONTACT: '/contact',
  PRIVACY: '/privacy-policy',
  TERMS: '/terms-of-service',
  DISCLAIMER: '/disclaimer',
  RISK_DISCLOSURE: '/risk-disclosure',
  COOKIE_POLICY: '/cookie-policy',
  TRADING_FAQ: '/trading-faq',
} as const;

export type TradingRoute = typeof TradingRoutes[keyof typeof TradingRoutes];